import { useMemo, useSyncExternalStore } from 'react';

/**
 * Stage UI (2026-10 redesign) — on/off state and theme.
 *
 * Stage is applied by setting `<html data-ui="stage">`. Every Stage style (tokens.css, the
 * zoom removal, the theme) is scoped to that attribute, so with it absent the app renders
 * exactly as before.
 *
 * Effective state = the user's explicit choice (localStorage 'snaplayer.stageUi' = 'on'|'off')
 * if they made one, otherwise the STAGE_UI feature flag (env VITE_FEATURE_STAGE_UI or the
 * remote per-hostname override). The flag is off by default everywhere.
 *
 * `?ui=stage` / `?ui=classic` in the URL records the choice too (handy for testing on dev/beta);
 * `?ui=auto` clears it.
 *
 * Production gate: on the production host (snaplayer.app) an "on" choice — from the URL or
 * saved earlier — counts only while the STAGE_UI flag is on there. Without that, anyone opening
 * a shared link with `?ui=stage` would switch Stage on permanently while it is still unfinished.
 * An "off" choice is always honoured. index.html's pre-paint script mirrors this host check.
 *
 * The desktop editor runs in a same-origin iframe with its own bundle, so changes are
 * broadcast through the native `storage` event as well as a same-document CustomEvent.
 *
 * This module has no app imports so it can move into the shared tokens package later.
 */

export const STAGE_UI_STORAGE_KEY = 'snaplayer.stageUi';
export const STAGE_THEME_STORAGE_KEY = 'snaplayer.theme';
/** Auto mode source: 'device' (follow the OS) or 'time' (light/dark by the clock). */
export const STAGE_THEME_AUTO_STORAGE_KEY = 'snaplayer.themeAuto';
/** Hours for the time-of-day Auto mode, "7-19" = light from 07:00, dark from 19:00. */
export const STAGE_THEME_HOURS_STORAGE_KEY = 'snaplayer.themeHours';
const CHANGE_EVENT = 'snaplayer:stage-ui-change';

export type StageUiPreference = 'on' | 'off';
/** 'system' is the "Auto" choice in the UI; StageThemeAuto says what Auto follows. */
export type StageTheme = 'light' | 'dark' | 'system';
export type StageThemeAuto = 'device' | 'time';

export interface StageThemeHours {
  /** Hour of day (0-23) the light theme starts. */
  lightFrom: number;
  /** Hour of day (0-23) the dark theme starts. */
  darkFrom: number;
}

export const DEFAULT_THEME_HOURS: StageThemeHours = { lightFrom: 7, darkFrom: 19 };

export function parseThemeHours(raw: string | null | undefined): StageThemeHours {
  const m = /^(\d{1,2})-(\d{1,2})$/.exec(raw ?? '');
  if (!m) return DEFAULT_THEME_HOURS;
  const lightFrom = Number(m[1]);
  const darkFrom = Number(m[2]);
  if (lightFrom > 23 || darkFrom > 23 || lightFrom === darkFrom) return DEFAULT_THEME_HOURS;
  return { lightFrom, darkFrom };
}

export function formatThemeHours(hours: StageThemeHours): string {
  return `${hours.lightFrom}-${hours.darkFrom}`;
}

/** The theme the clock picks for an hour (0-23). A window that wraps midnight is supported. */
export function themeForHour(hour: number, hours: StageThemeHours = DEFAULT_THEME_HOURS): 'light' | 'dark' {
  const { lightFrom, darkFrom } = hours;
  const light = lightFrom < darkFrom ? hour >= lightFrom && hour < darkFrom : hour >= lightFrom || hour < darkFrom;
  return light ? 'light' : 'dark';
}

function safeGet(key: string): string | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string | null): void {
  try {
    if (typeof window === 'undefined') return;
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode, blocked) — in-memory state still updates */
  }
}

export function getStageUiPreference(): StageUiPreference | null {
  const raw = safeGet(STAGE_UI_STORAGE_KEY);
  return raw === 'on' || raw === 'off' ? raw : null;
}

export function getStageTheme(): StageTheme {
  const raw = safeGet(STAGE_THEME_STORAGE_KEY);
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
}

export function getStageThemeAuto(): StageThemeAuto {
  return safeGet(STAGE_THEME_AUTO_STORAGE_KEY) === 'time' ? 'time' : 'device';
}

export function getStageThemeHours(): StageThemeHours {
  return parseThemeHours(safeGet(STAGE_THEME_HOURS_STORAGE_KEY));
}

/** What the clock says right now for the stored hours. */
export function getScheduledTheme(now: Date = new Date()): 'light' | 'dark' {
  return themeForHour(now.getHours(), getStageThemeHours());
}

/**
 * Snapshot string for hooks: 'device', or 'time:<from>-<to>:<light|dark now>'. A string keeps
 * useSyncExternalStore stable and changes when the stored hours or the clock's answer change.
 */
export function getStageThemeAutoSnapshot(): string {
  return getStageThemeAuto() === 'time' ? `time:${formatThemeHours(getStageThemeHours())}:${getScheduledTheme()}` : 'device';
}

/** Same hostname rule as src/config/domains.ts (kept local: this module has no app imports). */
export function isProductionHost(hostname?: string): boolean {
  const h = hostname ?? (typeof window === 'undefined' ? '' : window.location.hostname);
  return h === 'snaplayer.app' || h === 'www.snaplayer.app';
}

/**
 * Side-by-side testing host: `next.*` / `v2.*` (e.g. next.snaplayer.app, next.dev.snaplayer.app)
 * serve the same app and data with Stage ON by default, so the classic UI on the normal host and
 * the new UI can be compared in two tabs. localStorage is per-origin, so each host keeps its own
 * choice; `?ui=classic` still turns Stage off on a next.* host.
 */
export function isStageHost(hostname?: string): boolean {
  const h = hostname ?? (typeof window === 'undefined' ? '' : window.location.hostname);
  return /^(next|v2)\./i.test(h);
}

/**
 * Pure resolution rule: an explicit user choice wins, otherwise the flag decides.
 * On production (`production` true) an "on" choice only counts while the flag is on.
 */
export function resolveStageUi(flagOn: boolean, pref: StageUiPreference | null, production = false): boolean {
  if (pref === 'on') return production ? flagOn : true;
  if (pref === 'off') return false;
  return flagOn;
}

let flagOn = false;
let enabled = false;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((fn) => fn());
}

/** Write the current state onto <html>. Off removes every Stage attribute. */
function applyToDocument() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (enabled) {
    root.dataset.ui = 'stage';
    const theme = getStageTheme();
    if (theme === 'system') {
      // Auto: by the clock when the user picked hours, otherwise follow the device (no attribute).
      if (getStageThemeAuto() === 'time') root.dataset.theme = getScheduledTheme();
      else delete root.dataset.theme;
    } else root.dataset.theme = theme;
  } else {
    if (root.dataset.ui === 'stage') delete root.dataset.ui;
    delete root.dataset.theme;
    ensureFontAwesome();
  }
  syncClockTimer();
}

let clockTimer: ReturnType<typeof setInterval> | null = null;

/** While Auto-by-time is active, re-check the clock every 30s and when the tab comes back. */
function syncClockTimer() {
  if (typeof window === 'undefined') return;
  const need = enabled && getStageTheme() === 'system' && getStageThemeAuto() === 'time';
  if (need && !clockTimer) {
    clockTimer = setInterval(tickClock, 30_000);
    document.addEventListener('visibilitychange', tickClock);
  } else if (!need && clockTimer) {
    clearInterval(clockTimer);
    clockTimer = null;
    document.removeEventListener('visibilitychange', tickClock);
  }
}

function tickClock() {
  // Compare with what <html> shows, so changed hours and a passing hour boundary both apply.
  if (typeof document === 'undefined' || document.documentElement.dataset.theme === getScheduledTheme()) return;
  applyToDocument();
  emit();
}

/**
 * Classic still renders FontAwesome icons. index.html skips the FA script when the page boots in
 * Stage, so switching Stage off mid-session loads it then (no-op if already requested, and in
 * tests, where the loader from index.html does not exist).
 */
function ensureFontAwesome() {
  try {
    const loader = (window as unknown as { __slLoadFontAwesome?: () => void }).__slLoadFontAwesome;
    if (typeof loader === 'function') loader();
  } catch {
    /* ignore */
  }
}

function recompute() {
  const next = resolveStageUi(flagOn || isStageHost(), getStageUiPreference(), isProductionHost());
  const changed = next !== enabled;
  enabled = next;
  applyToDocument();
  if (changed) emit();
}

/** Called by the app shell once remote feature flags are known. */
export function setStageUiFlag(value: boolean): void {
  flagOn = value;
  recompute();
}

/** Record the user's choice. `null` clears it so the flag decides again. */
export function setStageUiPreference(pref: StageUiPreference | null): void {
  safeSet(STAGE_UI_STORAGE_KEY, pref);
  recompute();
  broadcast();
}

export function setStageTheme(theme: StageTheme): void {
  safeSet(STAGE_THEME_STORAGE_KEY, theme);
  applyToDocument();
  emit();
  broadcast();
}

/** Choose what Auto follows: the device, or the clock (with the saved hours). */
export function setStageThemeAuto(mode: StageThemeAuto): void {
  safeSet(STAGE_THEME_AUTO_STORAGE_KEY, mode === 'time' ? 'time' : null);
  applyToDocument();
  emit();
  broadcast();
}

export function setStageThemeHours(hours: StageThemeHours): void {
  // The same hour for both would leave no window: keep the hours as they were.
  if (hours.lightFrom === hours.darkFrom) return;
  const next = parseThemeHours(formatThemeHours(hours));
  safeSet(STAGE_THEME_HOURS_STORAGE_KEY, formatThemeHours(next));
  applyToDocument();
  emit();
  broadcast();
}

export function isStageUiEnabled(): boolean {
  return enabled;
}

function broadcast() {
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    /* ignore */
  }
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** React hook: true while Stage UI is on. Re-renders on toggle. */
export function useStageUi(): boolean {
  return useSyncExternalStore(subscribe, isStageUiEnabled, () => false);
}

/** React hook: the stored theme choice (only meaningful while Stage UI is on). */
export function useStageTheme(): StageTheme {
  return useSyncExternalStore(subscribe, getStageTheme, () => 'system' as StageTheme);
}

/** React hook: what Auto follows plus the hours and the clock's current answer. */
export function useStageThemeAuto(): { mode: StageThemeAuto; hours: StageThemeHours; scheduled: 'light' | 'dark' } {
  const snap = useSyncExternalStore(subscribe, getStageThemeAutoSnapshot, () => 'device');
  return useMemo(() => {
    if (!snap.startsWith('time:')) return { mode: 'device' as const, hours: getStageThemeHours(), scheduled: 'light' as const };
    const [, range, now] = snap.split(':');
    return { mode: 'time' as const, hours: parseThemeHours(range), scheduled: now === 'dark' ? ('dark' as const) : ('light' as const) };
  }, [snap]);
}

function readUrlOverride() {
  if (typeof window === 'undefined') return;
  try {
    const ui = new URLSearchParams(window.location.search).get('ui');
    // On production a URL can't opt anyone in; the flag (and the in-app toggle it unlocks) does.
    if (ui === 'stage') {
      if (!isProductionHost()) safeSet(STAGE_UI_STORAGE_KEY, 'on');
    }
    else if (ui === 'classic') safeSet(STAGE_UI_STORAGE_KEY, 'off');
    else if (ui === 'auto') safeSet(STAGE_UI_STORAGE_KEY, null);
  } catch {
    /* ignore */
  }
}

let initialized = false;

/** Idempotent boot: read URL + storage, apply to <html>, listen for other frames. */
export function initStageUi(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  readUrlOverride();
  recompute();
  const sync = () => {
    recompute();
    emit();
  };
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === STAGE_UI_STORAGE_KEY || event.key === STAGE_THEME_STORAGE_KEY || event.key === STAGE_THEME_AUTO_STORAGE_KEY || event.key === STAGE_THEME_HOURS_STORAGE_KEY) sync();
  });
  window.addEventListener(CHANGE_EVENT, sync);
}

/** Test helper: reset module state. */
export function __resetStageUiForTests(): void {
  flagOn = false;
  enabled = false;
  initialized = false;
  applyToDocument();
  emit();
}
